import React from 'react';
import * as Yup from 'yup';
import { useAuthentication } from 'signer-app/authentication';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { TextInput } from '@dropbox/dig-components/text_fields';
import {
  FormHelperText,
  FormRow,
  FormLabel,
} from '@dropbox/dig-components/form_row';
import { Button } from '@dropbox/dig-components/buttons';
import { Link, Text, Title } from '@dropbox/dig-components/typography';
import { UIIcon } from '@dropbox/dig-icons';
import { FailLine } from '@dropbox/dig-icons/assets';
import { Snackbar } from '@dropbox/dig-components/snackbar';
import { Box } from '@dropbox/dig-foundations';
import PrivacyTOSLinks from 'hellosign/components/auth/components/privacy-policy-and-tos';
import { useArkoseIntercept } from 'signer-app/authentication/use-arkose-intercept';
import { OrDivider } from 'hello-react/components/or-divider';
import { useOnLoginRedirectUrl } from './use-on-login-redirect-url';
import MarketingOpt from './marketing-opt';
import styles from './authentication.module.scss';
import { SignInButtons } from './sign-in-buttons';

const messages = defineMessages({
  dropboxLogInBanner: {
    id: '',
    description:
      'Notification message prompting users with Dropbox accounts to login on Dropbox',
    defaultMessage: 'Already have a Dropbox account?',
  },
  dropboxBannerLink: {
    id: '',
    description: 'Text for a link to redirect to Dropbox login page',
    defaultMessage: 'Sign in to access {brandName}',
  },
  showPassword: {
    id: '',
    description:
      "Hover text for a button which will show the user's password in plaintext when pressed.",
    defaultMessage: 'Show password',
  },
  hidePassword: {
    id: '',
    description:
      "Hover text for a button which will hide the user's password when pressed.",
    defaultMessage: 'Hide password',
  },
  error: {
    id: '',
    description: 'Error message',
    defaultMessage: 'Error',
  },
});

const yupEmail = Yup.string().email();

export default function ProgressiveCheckoutSignUpForm(): React.ReactElement | null {
  const intl = useIntl();
  const signInUrl = useOnLoginRedirectUrl('/account/logIn');
  const {
    state,
    update,
    meta,
    signUp,
    marketingMode,
    dropboxManagedAndSamlCheck,
    getDropboxManagedLoginLink,
  } = useAuthentication();

  const { dropboxManagedAndSamlLoginChecked, isSamlLogin, email, isLoading } =
    state;

  const [isEmailInvalid, setIsEmailInvalid] = React.useState(false);

  const arkoseSubmit = useArkoseIntercept(
    ({ arkoseToken }) => signUp({ arkoseToken }),
    state.email,
    meta?.arkoseEnabled,
    meta?.arkoseSignUpPublicKey,
  );

  const submitForm = async () => {
    if (!state.email.length || !yupEmail.isValidSync(state.email)) {
      setIsEmailInvalid(true);
      return;
    }

    if (!dropboxManagedAndSamlLoginChecked && email.length > 0) {
      await dropboxManagedAndSamlCheck({});
    }

    if (isSamlLogin) {
      return;
      // User not allowed to continue. Use SAML provider.
    }

    return arkoseSubmit();
  };
  const handleSubmit: React.HTMLAttributes<HTMLFormElement>['onSubmit'] = (
    evt,
  ) => {
    evt.preventDefault();

    return submitForm();
  };

  if (!meta) {
    // loading
    return null;
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <FormRow>
        <div className={styles.header}>
          <Title tagName="h1" className={styles.headerTitle}>
            <FormattedMessage
              id=""
              description="Title for the sign up page"
              defaultMessage="Create an account"
            />
          </Title>
        </div>
      </FormRow>

      {!state.isOfficeIntegration && (
        <>
          <FormRow>
            <SignInButtons
              googleSignInClientId={meta.googleSignInClientId}
              hideDropboxButton={true}
              redirectUrl={meta.redirectUrl}
              isApi={false}
              formType={'signup'}
              googleAnalyticsTrackingVariant={''}
              planName={meta.planData?.name}
            />
          </FormRow>
          <FormRow>
            <OrDivider />
          </FormRow>
        </>
      )}

      <FormRow>
        <FormLabel htmlFor="email-field">
          <Text size="small" color="faint">
            <FormattedMessage
              id=""
              description="Label for a text input field"
              defaultMessage="Email"
            />
          </Text>
        </FormLabel>
        <TextInput
          autoFocus
          formNoValidate
          name="email"
          id="email-field"
          isInvalid={!!state.error || isEmailInvalid || state.showDMASnackbar}
          aria-invalid={
            !!state.error || isEmailInvalid || state.showDMASnackbar
          }
          aria-errormessage="email-helper-text"
          aria-describedby="email-helper-text"
          value={state.email}
          disabled={state.isLoading}
          onChange={(e) => {
            setIsEmailInvalid(false);
            update('email', e.target.value);

            if (state.dropboxManagedAndSamlLoginChecked) {
              update('dropboxManagedAndSamlLoginChecked', false);
            }
          }}
        />
        {(isEmailInvalid || state.showDMASnackbar) && (
          <FormHelperText id="email-helper-text" variant="alert">
            <UIIcon
              src={FailLine}
              size="small"
              aria-label={intl.formatMessage(messages.error)}
            />
            {state.showDMASnackbar ? (
              <FormattedMessage
                id=""
                description="Error message on the sign up page that asks user to use Dropbox log in method"
                defaultMessage="There’s already a Dropbox account with this email. Continue with Dropbox."
              />
            ) : (
              <FormattedMessage
                id=""
                description="Error message on the sign up page"
                defaultMessage="Please enter a valid email address"
              />
            )}
          </FormHelperText>
        )}
      </FormRow>

      <FormRow>
        <FormLabel htmlFor="first-name-field">
          <Text size="small" color="faint">
            <FormattedMessage
              id=""
              description="Label for a text input field."
              defaultMessage="First name"
            />
          </Text>
        </FormLabel>
        <TextInput
          formNoValidate
          name="first-name-field"
          id="first-name-field"
          isInvalid={!!state.error}
          aria-invalid={!!state.error}
          aria-errormessage="first-name-helper-text"
          aria-describedby="first-name-helper-text"
          value={state.firstName ?? ''}
          disabled={state.isLoading}
          onChange={(e) => {
            update('firstName', e.target.value);
          }}
        />
      </FormRow>

      <FormRow>
        <FormLabel htmlFor="last-name-field">
          <Text size="small" color="faint">
            <FormattedMessage
              id=""
              description="Label for a text input field."
              defaultMessage="Last name"
            />
          </Text>
        </FormLabel>
        <TextInput
          formNoValidate
          name="last-name-field"
          id="last-name-field"
          isInvalid={!!state.error}
          aria-invalid={!!state.error}
          aria-errormessage="last-name-helper-text"
          aria-describedby="last-name-helper-text"
          value={state.lastName ?? ''}
          disabled={state.isLoading}
          onChange={(e) => {
            update('lastName', e.target.value);
          }}
        />
      </FormRow>

      <MarketingOpt
        marketingMode={marketingMode}
        state={state}
        update={update}
      />

      <FormRow>
        <Box>
          <Text variant="paragraph" size="small">
            <PrivacyTOSLinks
              tosUrl={meta.tosUrl}
              privacyPolicyUrl={meta.privacyPolicyUrl}
            />
          </Text>
        </Box>
      </FormRow>

      <FormRow>
        <div className={styles.buttons}>
          <Button
            type="submit"
            variant="primary"
            size="large"
            isLoading={isLoading}
            data-testid="create-account-btn"
          >
            <FormattedMessage
              id=""
              description="button text, when clicked user agrees to ToS and Privacy Policy and submits the form"
              defaultMessage="Agree and Sign up"
            />
          </Button>
        </div>
      </FormRow>

      <FormRow>
        <Text isBold={true}>
          <Link href={signInUrl} isBold={true}>
            &larr;&nbsp;
            <FormattedMessage
              id=""
              defaultMessage="All login options"
              description="link, text, proposes to user to go back to previous page with the whole list of login options"
            />
          </Link>
        </Text>
      </FormRow>

      <Snackbar.Position>
        <Snackbar
          open={state.showDMASnackbar}
          timeout={0}
          role="alert"
          preferComposition
        >
          <Snackbar.Content>
            <Snackbar.Message>
              <FormattedMessage
                id=""
                description="Notification message for existing Dropbox account to login on Dropbox"
                defaultMessage="Looks like there’s already a Dropbox account associated with this email."
              />
            </Snackbar.Message>
            <Snackbar.Actions>
              <Button
                variant="transparent"
                href={getDropboxManagedLoginLink}
                inverse
              >
                <FormattedMessage
                  id=""
                  description="Text for a link to redirect to Dropbox login page"
                  defaultMessage="Continue with Dropbox"
                />
              </Button>
            </Snackbar.Actions>
          </Snackbar.Content>
        </Snackbar>
      </Snackbar.Position>
    </form>
  );
}
